import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useStyles } from '../../../LawPortal.styles';

import { useAptorApi } from '../../../../../Api';
import { Module, UserContext } from '../../../../../Context/UserContext/UserContext';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { useIntl } from 'react-intl';
import { ArrowBack, Close } from '@material-ui/icons';
import { ButtonLink, HtmlView, Panel } from '../../../../../Components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Paper, Grid, Typography, Dialog, DialogTitle, DialogActions } from '@material-ui/core';
import start from '../../../../../Assets/Images/start.png';
import { CardItem } from '../../../../../Components/Home/Components/Card/Card';
import { CreatePlanWizard, loadWizardSession, clearWizardSession, WizardSession } from '../CreatePlanWizard';
import { EvaluationInfoEnum } from '../../../../../Context/EvaluationInfoEmun';

type PlanType = 'one-time' | 'recurring' | 'from-completed' | 'create' | 'start';

const PRIMARY = '#c4122f';

export const PlanningLK = React.memo(() => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const { hasAccessToModule, customerId, id: userId, customerFeatures, companyUnits } = useContext(UserContext);
  const { setHeader } = useContext(ModuleContext);

  const navigate = useNavigate();
  const location = useLocation();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [planType, setPlanType] = useState<PlanType>('start');
  const [editPlanData, setEditPlanData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [evalutionInfo, setEvalutionInfo] = useState<any>(null);
  const [savedSession, setSavedSession] = useState<WizardSession | null | undefined>(undefined);
  const [resumeSession, setResumeSession] = useState<WizardSession | null>(null);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [evalutionStepInfo, setEvalutionStepInfo] = useState<any>(null);

  // Default evaluation step information structure with bilingual fields (memoized to prevent infinite loops)
  const DEFAULT_STEP_INFO = useMemo(() => ({
    'select-flow': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'select-plan-type': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'select-plan': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'days': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'company-unit': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'dates': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'reminder': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'requirement-type': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'laws-table': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    'plan-name': {
      EvaluationInformationSv: '',
      EvaluationInformationEn: '',
    },
    // 'preview' excluded as per requirement
  }), []);

  // Fetch evaluation information
  const fetchEvaluationInfo = React.useCallback(() => {
    api
      .getLawportalevaluationSettings(EvaluationInfoEnum.EvaluationInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        setEvalutionInfo(result);
      })
      .catch((err) => {
        console.error('API error:', err);
        setEvalutionInfo(undefined);
      });
  }, [api, customerId, abortController]);

  // Fetch evaluation step information
  const fetchEvaluationStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.EvaluationStepInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setEvalutionStepInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setEvalutionStepInfo(DEFAULT_STEP_INFO);
          }
        } else {
          // Set default data if API returns null
          setEvalutionStepInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        // Set default data on error
        setEvalutionStepInfo(DEFAULT_STEP_INFO);
      });
  }, [api, customerId, abortController, DEFAULT_STEP_INFO]);

  useEffect(() => {
    fetchEvaluationInfo();
  }, [fetchEvaluationInfo]);

  useEffect(() => {
    fetchEvaluationStepInfo();
  }, [fetchEvaluationStepInfo]);

  // ── On mount: check for edit navigation OR existing saved session ──────────
  useEffect(() => {
    const state = location.state as any;

    if (state?.editPlan) {
      setEditPlanData(state.editPlan);
      setIsEditMode(true);
      setPlanType(state.editPlan.isDirect ? 'one-time' : 'recurring');
      setResumeSession(null);
      setSavedSession(null);
      setWizardOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const session = loadWizardSession(userId);
    setSavedSession(session);
  }, []); // run once on mount only

  const menuConfig = useMemo(() => {
 const hasAvvikelserLight = customerFeatures?.some((feature: any) => feature.value === 13);
    let menu: any = [];
    if (hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization)) {
      menu = [
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.planning.lek' }),
          to: `/law-portal/legal-compliance-control/planning`,
        },
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.carry.lek' }),
          to: `/law-portal/legal-compliance-control/carry`,
        },
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.lek' }),
          to: `/law-portal/legal-compliance-control/reports`,
        }
      ];
    } else {
      menu = [
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.carry.lek' }),
          to: `/law-portal/legal-compliance-control/carry`,
        },
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.lek' }),
          to: `/law-portal/legal-compliance-control/reports`,
        }
      ];
    }
    if (hasAvvikelserLight) {
      menu.push({
        title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.observations' }),
        to: `/law-portal/legal-compliance-control/observations`,
      })
    }

    return menu;
  }, [hasAccessToModule, formatMessage]);

  useEffect(() => {
    setHeader(
      formatMessage({ id: 'law-portal.legal.compliance.header', defaultMessage: 'Evaluation control' }),
      menuConfig,
    );
  }, [setHeader, formatMessage, menuConfig]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // FIX: handleStartClick no longer calls clearWizardSession or handleDiscardAndStart.
  // Session is preserved until user explicitly discards via ResumeBanner "Discard" button,
  // or cancels/finishes inside the wizard.
  const handleStartClick = () => {
    // If there's an ongoing saved session, show confirmation dialog
    if (savedSession) {
      setShowStartConfirm(true);
      return;
    }

    setEditPlanData(null);
    setIsEditMode(false);
    setResumeSession(null);
    setPlanType('start');
    setWizardOpen(true);
  };

  const handleStartConfirmResume = () => {
    setShowStartConfirm(false);
    handleResume();
  };

  const handleStartConfirmNew = () => {
    setShowStartConfirm(false);
    clearWizardSession(userId);
    setSavedSession(null);
    setEditPlanData(null);
    setIsEditMode(false);
    setResumeSession(null);
    setPlanType('start');
    setWizardOpen(true);
  };

  const handleResume = () => {
    if (!savedSession) return;
    setIsEditMode(false);
    setPlanType(savedSession.planType as PlanType);
    setResumeSession(savedSession);
    setSavedSession(null);
    setWizardOpen(true);
  };

  const handleDiscardAndStart = () => {
    clearWizardSession(userId);
    setSavedSession(null);
    setResumeSession(null);
    setEditPlanData(null);
    setIsEditMode(false);
    setPlanType('start');
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setEditPlanData(null);
    setResumeSession(null);
    const wasEdit = isEditMode;
    setIsEditMode(false);
    // if (!wasEdit) {
    const session = loadWizardSession(userId);
    setSavedSession(session);
    // }
  };

  const handleWizardback = () => {
    setWizardOpen(false);
    setEditPlanData(null);
    setResumeSession(null);
    setIsEditMode(false);
    // Always reload session — edit ho ya na ho
    const session = loadWizardSession(userId);
    setSavedSession(session);
  };

  const handlePlanCreated = () => {
    navigate('/law-portal/legal-compliance-control/carry');
  };

  // Handle evaluation step info update - re-fetch fresh data from API
  const handleEvaluationStepInfoUpdate = React.useCallback(() => {
    fetchEvaluationStepInfo();
  }, [fetchEvaluationStepInfo]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (savedSession === undefined) return null;

  return (
    <div className={classes.listWrapper}>
      {/* {wizardOpen && (
        <ButtonLink
          text={formatMessage({ id: 'component.back-button.back' })}
          onclick={() => handleWizardback()}
          to={''}
          icon={<ArrowBack />}
        />
      )} */}
      {/* <hr style={{ marginTop: '25px', marginBottom: '25px' }} /> */}

      {wizardOpen ? (
        <div className={classes.listWrapper}>
          <CreatePlanWizard
            open={wizardOpen}
            planType={planType}
            onClose={handleWizardClose}
            getCompliancePlanList={handlePlanCreated}
            editPlan={editPlanData}
            resumeSession={resumeSession}
            evalutionStepInfo={evalutionStepInfo}
            onEvaluationStepUpdate={handleEvaluationStepInfoUpdate}
          />
        </div>
      ) : (
        <>
          {/* ── Resume banner ── */}
          {savedSession && (
            <ResumeBanner
              session={savedSession}
              onResume={handleResume}
              onDiscard={() => { clearWizardSession(userId); setSavedSession(null); }}
              formatMessage={formatMessage}
              companyUnits={companyUnits}
            />
          )}

          {/* ── Single start card ── */}
          <Grid container spacing={2} style={{ marginTop: '40px' }} alignItems="stretch">

            {/* FIX: CardItem always calls handleStartClick.
                Previously: savedSession ? handleDiscardAndStart : handleStartClick
                That was silently wiping the session before user could see the Resume banner.
                Now only the ResumeBanner "Discard" button can explicitly delete a session. */}
            <CardItem
              label="law-portal.legal.compliance.sub-menu.planning.btn.start-evealution"
              // image={start}
              onClick={handleStartClick}
            />

            <Grid item xs style={{ display: 'flex' }}>
              <Panel
                title={formatMessage({ id: 'law-portal.legal.compliance.evaluation-control.panel.header' })}
                style={{ width: '100%', height: '100%', boxSizing: 'border-box', backgroundColor: "#f7f0f1", }}
              >
                <Typography variant="body1">
                  <HtmlView value={evalutionInfo} />
                </Typography>
              </Panel>
            </Grid>

          </Grid>
        </>
      )}

      {/* ── Start Confirmation Dialog ── */}
      <Dialog
        open={showStartConfirm}
        onClose={() => setShowStartConfirm(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          <div style={{ cursor: 'pointer' }} onClick={() => { setShowStartConfirm(false) }} ><Close /></div>

          {formatMessage({
            id: 'law-portal.evalution.plan.ongoing.work.title',
            defaultMessage: 'Stop, you have already an ongoing work. Do you want to resume or create a new?',
          })}
        </DialogTitle>
        <DialogActions style={{ padding: '16px 24px', justifyContent: 'space-between', gap: '8px' }}>
          <Button
            variant="contained"
            onClick={handleStartConfirmResume}
            style={{ backgroundColor: PRIMARY, color: '#fff', flex: 1, minWidth: '60px' }}
          >
            {formatMessage({
              id: 'law-portal.evalution.plan.btn.continue',
              defaultMessage: 'Resume',
            })}
          </Button>
          <Button
            variant="outlined"
            onClick={handleStartConfirmNew}
            style={{ color: PRIMARY, borderColor: PRIMARY, flex: 1, minWidth: '80px' }}
          >
            {formatMessage({
              id: 'lawportal.evalution-control.new',
              defaultMessage: 'New',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

PlanningLK.displayName = 'PlanningLK';

export default PlanningLK;

// ─────────────────────────────────────────────
// Resume Banner component
// ─────────────────────────────────────────────
const PLAN_TYPE_LABELS: Record<string, string> = {
  'one-time': 'law-portal.legal.compliance.type.planning.one.time',
  'recurring': 'law-portal.legal.compliance.type.planning.recurring.plan',
  'from-completed': 'law-portal.legal.compliance.type.planning.earlier.completed.plan',
};

const ResumeBanner = ({
  session,
  onResume,
  onDiscard,
  formatMessage,
  companyUnits,
}: {
  session: WizardSession;
  onResume: () => void;
  onDiscard: () => void;
  formatMessage: any;
  companyUnits?: any[];
}) => {
  // Determine the effective type to show in the banner:
  // 1. If planType is 'start' or 'create', check _resolvedFlow first:
  //    - If _resolvedFlow === 'from-completed' → show 'from-completed'
  //    - Otherwise fall back to _resolvedPlanType (one-time / recurring)
  // 2. For direct planTypes (one-time, recurring, from-completed), use as-is
  const effectiveType = (() => {
    if (session.planType === 'start' || session.planType === 'create') {
      if (session.stepData?._resolvedFlow === 'from-completed') return 'from-completed';
      return session.stepData?._resolvedPlanType;
    }
    return session.planType;
  })();
  const planLabel = effectiveType ? PLAN_TYPE_LABELS[effectiveType as string] : undefined;
  const stepNumber = session.currentStep;

  // Calculate total steps based on plan type and resolutions (same logic as CreatePlanWizard)
  const calculateTotalSteps = () => {
    const steps: string[] = [];
    const resolvedFlow = session.stepData?._resolvedFlow ?? 'create';
    const resolvedPlanType = session.stepData?._resolvedPlanType ?? 'one-time';

    if (session.planType === 'start') {
      steps.push('select-flow');
      if (resolvedFlow === 'from-completed') {
        steps.push('select-plan');
        steps.push('days');
      } else {
        steps.push('select-plan-type');
        if (resolvedPlanType === 'recurring') {
          steps.push('days');
        }
      }
    } else if (session.planType === 'create') {
      steps.push('select-plan-type');
      if (resolvedPlanType === 'recurring') steps.push('days');
    } else {
      if (session.planType === 'from-completed') steps.push('select-plan');
      if (session.planType === 'recurring' || session.planType === 'from-completed') steps.push('days');
    }

    // Only add company-unit step if more than one company unit
    if (!companyUnits || companyUnits.length !== 1) {
      steps.push('company-unit');
    }
    steps.push('dates');
    steps.push('reminder');
    steps.push('requirement-type');
    steps.push('laws-table');
    steps.push('plan-name');
    steps.push('preview');
    return steps.length;
  };

  const totalSteps = calculateTotalSteps();

  return (
    <Paper
      variant="outlined"
      style={{
        padding: '16px 24px',
        marginBottom: '24px',
        marginTop: '24px',
        borderColor: PRIMARY,
        borderWidth: '1.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <Box>
        <Typography variant="subtitle1" style={{ fontWeight: 700, color: PRIMARY }}>
          {formatMessage({ id: 'law-portal.evalution.plan.unfinished.plan.text' })}
        </Typography>
        <Typography variant="body2" style={{ color: '#555', marginTop: '4px' }}>
          {planLabel && (
            <>
              <strong>{formatMessage({ id: 'admin.sendouts.table.type' })}:</strong>{' '}
              {formatMessage({ id: planLabel })}
              &nbsp;·&nbsp;
            </>
          )}
          <strong>{formatMessage({ id: 'law-portal.evalution.plan.text.step' })}:</strong> {formatMessage({ id: 'lawportal.evalution-control.step' }, { currentStep: stepNumber, totalSteps })}
        </Typography>
      </Box>
      <Box style={{ display: 'flex', gap: '10px' }}>
        <Button
          variant="contained"
          onClick={onResume}
          style={{ background: PRIMARY, color: '#fff', fontWeight: 700, textTransform: 'none' }}
        >
          {formatMessage({ id: 'law-portal.evalution.plan.btn.resume', defaultMessage: 'Resume' })}
        </Button>
        <Button
          variant="outlined"
          onClick={onDiscard}
          style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700, textTransform: 'none' }}
        >
          {formatMessage({ id: 'law-portal.evalution.plan.btn.delete', defaultMessage: 'Delete' })}
        </Button>
      </Box>
    </Paper>
  );
};